import React, { useState, useEffect, useRef } from 'react';

const ConsentForm = ({ onConsent, onBypass }) => {
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const consentModalRef = useRef(null);

  const canProceed = termsAccepted && ageConfirmed;

  // Auto-scroll to bottom when checkboxes are checked
  useEffect(() => {
    if (termsAccepted || ageConfirmed) {
      const modal = consentModalRef.current;
      if (modal) {
        modal.scrollTo({
          top: modal.scrollHeight,
          behavior: 'smooth'
        });
      }
    }
  }, [termsAccepted, ageConfirmed]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canProceed) return;
    
    setIsSubmitting(true);
    
    // Store consent in localStorage
    localStorage.setItem('midi-llm-consent', JSON.stringify({
      timestamp: new Date().toISOString(),
      termsAccepted: true,
      ageConfirmed: true
    }));
    
    // Small delay for better UX
    setTimeout(() => {
      onConsent();
    }, 500);
  };

  const handleBypass = () => {
    if (onBypass) {
      onBypass();
    }
  };

  return (
    <div className="consent-overlay">
      <div className="consent-modal" ref={consentModalRef}>
        <div className="consent-header">
          <h2>🎸 Welcome to MIDI-LLM Live Demo</h2>
        </div>
        
        <div className="consent-content">
          <p className="consent-intro">
            Before you begin, please review our data collection practices:
          </p>
          
          <div className="consent-details">
            <p>
              <strong>Research Purpose:</strong> This demo helps us improve text-to-MIDI music generation models 
              through your voluntary interactions and feedback.
            </p>
            
            <p>
              <strong>What We Collect:</strong> Only the text prompts you enter and any optional feedback 
              you choose to provide (ratings or comments). No personal information is collected or stored.
            </p>
            
            <p>
              <strong>Data Security:</strong> All data is stored securely with access limited to our research team, 
              and will be anonymized before any publication.
            </p>
            
            <p>
              <strong>Your Choice:</strong> Participation is completely voluntary. You can use the demo 
              without providing feedback, and you can stop at any time.
            </p>
            
            <p>
              <strong>Age Requirement:</strong> To ensure we meet institutional review board (IRB) guidelines, 
              we require that participants be at least 18 years old. 
            </p>
          </div>

          <form onSubmit={handleSubmit} className="consent-form">
            <div className="consent-checkboxes">
              <label className="consent-checkbox-item">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  disabled={isSubmitting}
                />
                <span className="checkmark"></span>
                <span className="checkbox-text">
                  I agree to the terms described above and consent to participate in this research
                </span>
              </label>

              <label className="consent-checkbox-item">
                <input
                  type="checkbox"
                  checked={ageConfirmed}
                  onChange={(e) => setAgeConfirmed(e.target.checked)}
                  disabled={isSubmitting}
                />
                <span className="checkmark"></span>
                <span className="checkbox-text">
                  I certify that I am at least 18 years old
                </span>
              </label>
            </div>

            <div className="consent-actions">
              <button
                type="submit"
                className={`consent-submit-btn ${canProceed ? 'enabled' : 'disabled'}`}
                disabled={!canProceed || isSubmitting}
              >
                {isSubmitting ? 'Just a moment ...' : '🪗 Start Creating Music'}
              </button>
              
              <button
                type="button"
                className="consent-bypass-btn"
                onClick={handleBypass}
                disabled={isSubmitting}
              >
                Proceed without live demo access
              </button>
            </div>
          </form>
          
          {/* <p className="consent-footer">
            Questions about this research? Contact our team through the repository linked in our demo.
          </p> */}
        </div>
      </div>
    </div>
  );
};

export default ConsentForm; 